// AGC: Assignees Service - Fetches assignable users for task assignment
// AGC: Returns "Me" (current user) + team members managed by current user

import { getUsersByManager, type UserProfile } from './firebase-auth'

/**
 * AGC: Assignee option for dropdown selection
 */
export interface AssigneeOption {
  id: string              // AGC: Firebase UID or 'me' for current user
  uid: string             // AGC: Firebase UID
  displayName: string     // AGC: Display name for dropdown
  email?: string          // AGC: Email address
  avatar?: string         // AGC: Avatar URL
  isCurrentUser: boolean  // AGC: True if this is the logged-in user
  role?: string           // AGC: User role (manager, user, etc.)
  subtype?: string        // AGC: User subtype (operador, encarregado, etc.)
}

/**
 * AGC: Get all assignable users for task assignment
 * AGC: Returns current user first, then team members
 * @param currentUser - The currently logged-in user
 * @returns Array of AssigneeOption for dropdown
 */
export async function getAssignableUsers(
  currentUser: { uid: string; displayName?: string | null; email?: string | null; photoURL?: string | null } | null
): Promise<AssigneeOption[]> {
  const assignees: AssigneeOption[] = []

  // AGC: Always add current user as first option ("Me")
  if (currentUser?.uid) {
    assignees.push({
      id: 'me',
      uid: currentUser.uid,
      displayName: currentUser.displayName || currentUser.email || 'Eu',
      email: currentUser.email || undefined,
      avatar: currentUser.photoURL || undefined,
      isCurrentUser: true,
      role: 'manager',
    })

    // AGC: Fetch team members from BOTH systems
    // AGC: System 1: 'users' collection (created via TeamManagement/createTeamMember)
    // AGC: System 2: 'teamMembers' collection (created via AddTeamMember)
    try {
      // AGC: System 1: Get users from 'users' collection (created via TeamManagement)
      const usersFromUsersCollection = await getUsersByManager(currentUser.uid)
      
      // AGC: System 2: Get team members from 'teamMembers' collection (created via AddTeamMember)
      let teamMembersFromTeamCollection: any[] = []
      try {
        const { getTeamMembers } = await import('./teamService')
        const { getIdToken } = await import('./firebase-auth')
        const token = await getIdToken()
        if (token) {
          teamMembersFromTeamCollection = await getTeamMembers(token)
        }
      } catch (teamCollectionError) {
        console.warn('⚠️ AGC: [ASSIGNEES_SERVICE] Could not fetch from teamMembers collection:', teamCollectionError)
        // AGC: Continue with users collection only
      }
      
      console.log(`🔍 AGC: [ASSIGNEES_SERVICE] Fetched ${usersFromUsersCollection.length} users from 'users' collection`)
      console.log(`🔍 AGC: [ASSIGNEES_SERVICE] Fetched ${teamMembersFromTeamCollection.length} members from 'teamMembers' collection`)
      
      let activeCount = 0
      let inactiveCount = 0
      const addedEmails = new Set<string>() // AGC: Track added emails to avoid duplicates
      
      // AGC: Add users from 'users' collection (System 1)
      for (const member of usersFromUsersCollection) {
        // AGC: Only include active team members
        if (member.isActive !== false) {
          const email = member.email?.toLowerCase() || ''
          if (!addedEmails.has(email) && email) {
            assignees.push({
              id: member.uid,
              uid: member.uid,
              displayName: member.displayName || member.email || 'Membro',
              email: member.email,
              avatar: member.photoURL,
              isCurrentUser: false,
              role: member.role,
              subtype: member.subtype,
            })
            addedEmails.add(email)
            activeCount++
            console.log(`  ✅ AGC: [ASSIGNEES_SERVICE] Added user from 'users' collection: ${member.displayName} (${member.uid})`)
          }
        } else {
          inactiveCount++
          console.log(`  ⚠️ AGC: [ASSIGNEES_SERVICE] Skipped inactive member from 'users': ${member.displayName} (${member.uid})`)
        }
      }
      
      // AGC: Add team members from 'teamMembers' collection (System 2)
      for (const member of teamMembersFromTeamCollection) {
        const email = member.email?.toLowerCase() || ''
        // AGC: Skip if already added from 'users' collection (avoid duplicates by email)
        // AGC: Also skip cancelled invitations
        if (!addedEmails.has(email) && email && member.invitationStatus !== 'cancelled') {
          assignees.push({
            id: member.uid || member.id,
            uid: member.uid || member.id,
            displayName: member.displayName,
            email: member.email,
            avatar: member.avatar,
            isCurrentUser: false,
            role: member.role || 'user',
            subtype: undefined, // AGC: teamMembers don't have subtype
          })
          addedEmails.add(email)
          activeCount++
          console.log(`  ✅ AGC: [ASSIGNEES_SERVICE] Added member from 'teamMembers' collection: ${member.displayName} (${member.uid || member.id})`)
        } else if (addedEmails.has(email)) {
          console.log(`  ⏭️ AGC: [ASSIGNEES_SERVICE] Skipped duplicate from 'teamMembers': ${member.displayName} (already in 'users')`)
        }
      }

      console.log(`✅ AGC: [ASSIGNEES_SERVICE] Loaded ${assignees.length} assignable users (1 self + ${activeCount} active team members from both systems, ${inactiveCount} inactive skipped)`)
    } catch (error) {
      console.error('❌ AGC: [ASSIGNEES_SERVICE] Error fetching team members:', error)
      // AGC: Continue with just the current user if team fetch fails
    }
  }

  return assignees
}

/**
 * AGC: Get assignee display info by UID
 * AGC: Useful for displaying assignee info when viewing a task
 * @param uid - The Firebase UID of the assignee
 * @param currentUser - The currently logged-in user
 * @returns AssigneeOption or null if not found
 */
export async function getAssigneeByUid(
  uid: string | null,
  currentUser: { uid: string; displayName?: string | null; email?: string | null; photoURL?: string | null } | null
): Promise<AssigneeOption | null> {
  if (!uid) return null

  const assignees = await getAssignableUsers(currentUser)
  return assignees.find(a => a.uid === uid) || null
}

/**
 * AGC: Format assignee for display
 * AGC: Returns a user-friendly string for the assignee
 * @param assignee - The assignee option
 * @returns Formatted display string
 */
export function formatAssigneeDisplay(assignee: AssigneeOption | null): string {
  if (!assignee) return 'Não atribuído'
  if (assignee.isCurrentUser) return 'Eu'
  return assignee.displayName
}

/**
 * AGC: Get assignee avatar URL or initials
 * @param assignee - The assignee option
 * @returns Object with avatar URL or initials
 */
export function getAssigneeAvatar(assignee: AssigneeOption | null): { type: 'url' | 'initials'; value: string } {
  if (!assignee) {
    return { type: 'initials', value: '?' }
  }
  
  if (assignee.avatar) {
    return { type: 'url', value: assignee.avatar }
  }
  
  // AGC: Generate initials from display name
  const initials = assignee.displayName
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
  
  return { type: 'initials', value: initials || '?' }
}

